'use client'

import Link from '@/components/client/seo/link'
import { trackCustomEvent } from '@/hooks/client/use-analytics-engine'
import { useCompareDockHeight } from '@/hooks/client/use-compare-dock-height'
import { useCompared } from '@/hooks/client/use-compared'
import { useTranslation } from '@/hooks/client/use-translation'
import type { Locale } from '@/i18n'
import SvgXmark from '@/icons/thin/Xmark'
import { EventType } from '@/lib/analytics/events'
import { localizedHref } from '@/lib/utils/localized-href'

export interface ComparedCandidate {
  /** Release slug — the shared `ht-comparisons` set's currency (same as the
   *  directory's toggles). */
  slug: string
  /** Canonical product slug — what /hearing-aids/compare/a-vs-b URLs are made
   *  of (the compare tree is keyed by product, not release). */
  productSlug: string
  fullName: string
}

// Slim fixed-bottom comparison bar for the brand-lineup blok. Deliberately NOT
// the directory's CompareBar (that one is coupled to DirectoryRelease +
// filter presentation): this takes the lineup's own families and appears once
// any of them is selected. The compared set stores RELEASE slugs; compare
// URLs need PRODUCT slugs (the directory maps via pickCompareSlug + its
// filters), so the link here carries the canonical product slug of each
// selected candidate — selections made on other pages stay in the set but
// can't be mapped from here and are left out of the link.
export const ComparedBar = ({ candidates, locale }: { candidates: ComparedCandidate[]; locale: Locale }) => {
  const { compared, toggle } = useCompared()
  const { t } = useTranslation()

  const selected = candidates.filter((c) => compared.has(c.slug))
  const dockRef = useCompareDockHeight<HTMLDivElement>(selected.length)
  if (selected.length === 0) {
    return null
  }

  const canCompare = selected.length >= 2
  const compareHref = localizedHref(locale, `/hearing-aids/compare/${selected.map((c) => c.productSlug).join('-vs-')}`)

  return (
    <div
      ref={dockRef}
      role="region"
      aria-label={t('directory.dock.label', 'Your comparison')}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 shadow-[0_-4px_16px_rgba(15,23,42,0.08)] backdrop-blur-sm"
    >
      <div className="mx-auto flex w-full max-w-[1246px] flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
        <span className="text-navy/60 shrink-0 text-sm font-medium">
          {t('brandLineup.selectedCount', '{{count}} selected', { count: selected.length })}
        </span>
        <ul className="order-last flex w-full min-w-0 flex-wrap items-center gap-2 sm:order-none sm:w-auto sm:flex-1 sm:basis-64">
          {selected.map((candidate) => (
            <li
              key={candidate.slug}
              className="text-navy flex max-w-full min-w-0 items-center gap-1.5 rounded-full bg-slate-100 pr-0.5 pl-3 text-sm"
            >
              <span className="min-w-0 max-w-[180px] truncate">{candidate.fullName}</span>
              <button
                type="button"
                data-tracking="ignore"
                aria-label={t('aria.removeFromCompare', 'Remove from comparison')}
                onClick={() => toggle(candidate.slug)}
                className="focus-visible:outline-lapis flex size-11 min-h-[44px] min-w-[44px] shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-slate-200 focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <SvgXmark className="text-navy h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
        {canCompare ? (
          <Link
            prefetch={false}
            href={compareHref}
            data-tracking="ignore"
            onClick={() => trackCustomEvent('brand-lineup--compare-click', EventType.Click, {})}
            className="bg-navy ml-auto shrink-0 cursor-pointer rounded-full px-5 py-2 text-sm font-semibold text-white no-underline"
          >
            {t('brandLineup.compare', 'Compare')}
          </Link>
        ) : (
          <span className="text-navy/70 min-w-0 flex-1 text-sm sm:flex-none">
            {t('brandLineup.compareHint', 'Select at least two to compare')}
          </span>
        )}
      </div>
    </div>
  )
}
